import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import classNames from 'classnames';
import useUser from 'utils/useUser';
import { isMobile } from 'utils/deviceHelper';
import DraggableIcon from 'components/atoms/icons/draggable-icon.svg?react';
import CloseIcon from 'assets/img/close.svg?react';
import ChevronTop from 'components/atoms/icons/chevron-top';
import BaseButton from 'components/atoms/baseButton';
import { useTranslation } from 'react-i18next';
import { useWindow } from 'infrastructure/providers/window-manager-provider';
import useKey from 'infrastructure/hooks/utils/use-key';

import s from './styles.module.scss';

import type { FC } from 'react';

const BOUNDARY_LIMIT = 40;
let globalOpenedModalCount = 0;
const offsetStep = 20;

interface Props {
  title?: string | React.ReactNode;
  toggle: (arg?: boolean) => void;
  isOpen: boolean;
  children: React.ReactNode;
  subTitle?: string | React.ReactNode;
  modalClassName?: string;
  bodyClassName?: string;
  headerClassName?: string;
  titleClassName?: string;
  subTitleClassName?: string;
  footerClassName?: string;
  footer?: React.ReactNode | false;
  subHeader?: React.ReactNode;
  onSave?: () => void;
  dataCy?: string;
  saveLabel?: string;
  bodyRef?: React.LegacyRef<HTMLDivElement> | undefined;
  rightTitle?: React.ReactNode;
  isMinimized?: boolean;
  setIsMinimized?: (isMinimized: boolean) => void;
  withStackOffset?: boolean;
  isSaveLoading?: boolean;
  isSaveDisabled?: boolean;
  saveButtonTitle?: string;
  id?: string;
  destroyOnClose?: boolean;
  /**
   * Suppresses the modal's own header — title, drag grip and close — for
   * children that draw their own. The child is then responsible for a close
   * affordance and for carrying data-draggable on whatever should start a drag.
   */
  headerless?: boolean;
  /**
   * The Escape listener is document-level and unconditional, so any Escape
   * pressed anywhere on the page closes the modal. Turn it off for modals
   * where closing has side effects a stray keypress must not trigger.
   */
  closeOnEscape?: boolean;
}

const DraggableModal: FC<Props> = ({
  title,
  subTitle,
  children,
  toggle,
  isOpen,
  modalClassName,
  bodyClassName,
  headerClassName,
  footerClassName,
  footer,
  subHeader,
  titleClassName,
  subTitleClassName,
  onSave,
  saveLabel,
  isSaveLoading,
  isSaveDisabled,
  dataCy = 'draggableModal',
  rightTitle,
  isMinimized,
  setIsMinimized,
  withStackOffset = false, // if you use this flag, wrap your modal like {open && <Modal withStackOffset={true} /> }
  saveButtonTitle,
  bodyRef,
  id: modalId,
  destroyOnClose = true,
  headerless = false,
  closeOnEscape = true,
  ...rest
}) => {
  const { key } = useKey();
  const id = `draggable-modal-${key}`;
  const { focus, zIndex } = useWindow(id);
  const modalRef = useRef<HTMLDivElement>(null);
  const { deviceType } = useUser();
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({
    x: isMobile(deviceType) ? 10 : 170 + offsetStep * globalOpenedModalCount,
    y: 14 + offsetStep * globalOpenedModalCount,
  });
  const [defaultPosition, setDefaultPosition] = useState({
    x: isMobile(deviceType) ? 10 : 170 + offsetStep * globalOpenedModalCount,
    y: 14 + offsetStep * globalOpenedModalCount,
  });
  const [initialPosition, setInitialPosition] = useState({ x: 0, y: 0 });
  const getModalPosition = () => {
    if (isMinimized) {
      return {
        top: window.innerHeight - 60,
        left: window.innerWidth - 400,
      };
    }
    return {
      top: position.y,
      left: position.x,
    };
  };

  const style = {
    ...getModalPosition(),
    zIndex: 1000 + zIndex,
  };

  const { t } = useTranslation();

  const modalToggle = () => toggle(!isOpen);

  const toggleMinimize = () => {
    if (isMinimized) {
      setPosition(defaultPosition);
      setIsMinimized?.(false);
    } else {
      setDefaultPosition(position);
      setIsMinimized?.(true);
    }
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    const targetEl = e.target as HTMLElement;
    e.stopPropagation();
    if (targetEl?.dataset?.draggable !== 'true') return;

    setIsDragging(true);
    setInitialPosition({
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    });
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (isDragging) {
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      if (
        e.clientY <= BOUNDARY_LIMIT ||
        e.clientX <= BOUNDARY_LIMIT ||
        e.clientX >= viewportWidth - BOUNDARY_LIMIT ||
        e.clientY >= viewportHeight - BOUNDARY_LIMIT
      )
        return;

      const newPosition = {
        x: e.clientX - initialPosition.x,
        y: e.clientY - initialPosition.y,
      };

      setPosition(newPosition);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  useEffect(() => {
    if (!closeOnEscape) return undefined;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        toggle();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [toggle, closeOnEscape]);

  useEffect(() => {
    globalOpenedModalCount++;

    return () => {
      globalOpenedModalCount = Math.max(0, globalOpenedModalCount - 1);
    };
  }, []);

  useLayoutEffect(() => {
    if (isOpen && !destroyOnClose && modalRef.current) {
      const newPos = {
        x: isMobile(deviceType)
          ? 10
          : 170 + offsetStep * globalOpenedModalCount,
        y: 14 + offsetStep * globalOpenedModalCount,
      };
      setPosition(newPos);
      setDefaultPosition(newPos);
      return;
    }

    if (modalRef.current) {
      const newPosition = {
        x: isMobile(deviceType)
          ? 10
          : window.innerWidth / 2 - modalRef.current.offsetWidth / 2,
        y: isMobile(deviceType)
          ? 14 + offsetStep * globalOpenedModalCount
          : window.innerHeight / 2 - modalRef.current.offsetHeight / 2,
      };
      setPosition(newPosition);
      setDefaultPosition(newPosition);
    }
  }, [deviceType, modalRef, destroyOnClose, isOpen]);

  if (!isOpen && destroyOnClose) return <></>;

  return (
    <div
      {...rest}
      ref={withStackOffset ? undefined : modalRef}
      id={modalId ?? id}
      style={style}
      className={classNames(s.modal, modalClassName, {
        [s.minimized]: isMinimized,
        [s.open]: isOpen,
      })}
      onMouseDown={handleMouseDown}
      data-cy={`${dataCy}`}
      onFocus={focus}
      tabIndex={-1}
    >
      {!headerless && (
        <div
          data-draggable
          data-drag={isDragging}
          className={classNames(s.header, headerClassName)}
        >
          <div className={s['title-wrapper']} data-draggable>
            <div>
              <h4 className={classNames(s.title, titleClassName)}>{title}</h4>
              {subTitle && (
                <h5 className={classNames(s.subtitle, subTitleClassName)}>
                  {subTitle}
                </h5>
              )}
            </div>
            {rightTitle && <div className={s['right-title']}>{rightTitle}</div>}
          </div>
          <div className={s['close-container']}>
            <DraggableIcon
              width={32}
              height={32}
              className={classNames(s['draggable-icon'], s.button)}
            />
            {typeof setIsMinimized === 'function' && (
              <BaseButton
                className={s.button}
                onClick={toggleMinimize}
                icon={
                  isMinimized ? (
                    <ChevronTop className={s['close-icon']} />
                  ) : (
                    <ChevronTop
                      className={s['close-icon']}
                      style={{ transform: 'rotate(180deg)' }}
                    />
                  )
                }
                type="secondary"
              />
            )}
            <BaseButton
              className={s.button}
              onClick={modalToggle}
              icon={<CloseIcon className={s['close-icon']} />}
              type="secondary"
            />
          </div>
        </div>
      )}
      {subHeader && <div className={s['sub-header']}>{subHeader}</div>}
      <>
        <div
          ref={bodyRef}
          className={classNames(s.body, bodyClassName, { hidden: isMinimized })}
        >
          {children}
        </div>
        {footer !== false && (
          <div
            className={classNames(s.footer, footerClassName, {
              hidden: isMinimized,
            })}
            data-draggable
            data-drag={isDragging}
          >
            {footer ?? (
              <BaseButton
                title={saveButtonTitle}
                label={saveLabel || t('controls.save')}
                onClick={onSave}
                loading={isSaveLoading}
                disabled={isSaveDisabled}
              />
            )}
          </div>
        )}
      </>
    </div>
  );
};

export default DraggableModal;
